import { useCallback, useEffect } from 'react'

import { usePollingEffect } from '../../../apps/professional-app/fa/common/hooks/usePollingEffect'

import { DocumentSummaryExecutionResult } from '../../../models/ProcessDocument'

import { selectDocumentBatchPollingEnabled, setDocumentBatch } from '../../../store/processDocument/documentBatch'
import { selectDocumentPollingEnabled, setDocument, setSelectedDocumentType } from '../../../store/processDocument/document'
import { useLazyGetDocumentBatchByIdQuery } from '../../../store/ai/documentBatches'
import { useLazyGetAiAgentResultQuery } from '../../../store/ai/aiAgentResult'
import { useLazyGetDocumentByIdQuery } from '../../../store/ai/documents'
import { setMessages } from '../../../store/streamingChat/streamingChat'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { selectAccountId } from '../../../store/auth/authSlice'
import { AIAgentType } from '../../../models/AIAgent'
import {
  selectDocumentSummaryPollingEnabled,
  setDocumentIsNotRelatedToClient,
  setDocumentSummaryAiAgentId,
  setDocumentSummaryResult,
  setDocumentSummarySyncStatus,
} from '../../../store/processDocument/documentSummary'
import { selectDocumentTypes } from '../../../store/processDocument/documentTypes'

export const usePollDocumentBatch = (documentBatchId: string) => {
  const dispatch = useAppDispatch()

  const accountId = useAppSelector(selectAccountId)
  const documentBatchPollingEnabled = useAppSelector(selectDocumentBatchPollingEnabled)

  const [triggerGetDocumentBatchById] = useLazyGetDocumentBatchByIdQuery()

  const getDocumentBatch = useCallback(async () => {
    const documentBatch = await triggerGetDocumentBatchById({ accountId, documentBatchId }).unwrap()
    if (!documentBatch) {
      return
    }
    dispatch(setDocumentBatch(documentBatch))
  }, [accountId, dispatch, documentBatchId, triggerGetDocumentBatchById])

  const { start: startDocumentBatchPolling, stop: stopDocumentBatchPolling } = usePollingEffect(getDocumentBatch, [], {
    interval: 5000,
    leading: false,
    stopAfterFailedAttempts: 5,
  })

  useEffect(() => {
    if (documentBatchPollingEnabled) {
      startDocumentBatchPolling()
    } else {
      stopDocumentBatchPolling()
    }
  }, [documentBatchPollingEnabled, startDocumentBatchPolling, stopDocumentBatchPolling])
}

export const usePollDocument = (documentId: string) => {
  const dispatch = useAppDispatch()

  const accountId = useAppSelector(selectAccountId)
  const documentPollingEnabled = useAppSelector(selectDocumentPollingEnabled)
  const documentTypes = useAppSelector(selectDocumentTypes)

  const [triggerGetDocumentById] = useLazyGetDocumentByIdQuery()

  const getDocument = useCallback(async () => {
    if (!documentTypes.length) {
      return
    }

    const document = await triggerGetDocumentById({ accountId, documentId }).unwrap()
    if (!document) {
      return
    }

    dispatch(setDocument(document))

    const { typeId } = document.typeProcessing

    const documentType = documentTypes.find((documentType) => documentType.id === typeId)
    if (!documentType) {
      return
    }

    dispatch(setSelectedDocumentType(documentType))
  }, [accountId, dispatch, documentId, documentTypes, triggerGetDocumentById])

  const { start: startDocumentPolling, stop: stopDocumentPolling } = usePollingEffect(getDocument, [], {
    interval: 5000,
    leading: false,
    stopAfterFailedAttempts: 5,
  })

  useEffect(() => {
    if (documentPollingEnabled) {
      startDocumentPolling()
    } else {
      stopDocumentPolling()
    }
  }, [documentPollingEnabled, startDocumentPolling, stopDocumentPolling])
}

export const usePollDocumentSummary = (documentId: string) => {
  const dispatch = useAppDispatch()

  const accountId = useAppSelector(selectAccountId)

  const documentSummaryPollingEnabled = useAppSelector(selectDocumentSummaryPollingEnabled)

  const [triggerGetAiAgentResult] = useLazyGetAiAgentResultQuery()

  const getDocumentSummary = useCallback(async () => {
    const aiAgentResult = await triggerGetAiAgentResult({ accountId, type: AIAgentType.DOCUMENT_SUMMARY, resultKey: documentId }).unwrap()
    if (!aiAgentResult) {
      return
    }

    const { originalAiAgentId, result, interaction, syncStatus } = aiAgentResult
    const { aiAgentId, interactions = [] } = interaction ?? {}

    const { summary, documentIsNotRelatedToClient } = result as DocumentSummaryExecutionResult

    dispatch(setDocumentSummaryAiAgentId(aiAgentId ?? originalAiAgentId))
    dispatch(setDocumentSummaryResult({ summary }))
    dispatch(setMessages(interactions.map((i) => i.result)))
    dispatch(setDocumentSummarySyncStatus(syncStatus))

    if (documentIsNotRelatedToClient) {
      dispatch(setDocumentIsNotRelatedToClient(true))
    }
  }, [accountId, dispatch, documentId, triggerGetAiAgentResult])

  const { start: startDocumentSummaryPolling, stop: stopDocumentSummaryPolling } = usePollingEffect(getDocumentSummary, [], {
    interval: 5000,
    leading: false,
    stopAfterFailedAttempts: 5,
  })

  useEffect(() => {
    if (documentSummaryPollingEnabled) {
      startDocumentSummaryPolling()
    } else {
      stopDocumentSummaryPolling()
    }
  }, [documentSummaryPollingEnabled, startDocumentSummaryPolling, stopDocumentSummaryPolling])
}
